import { Base } from 'components/Containers/BaseContainer';
import { ThemePalette } from 'mui.theme';
import { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import {
  aggregateCostData,
  extractSelectedAssetsTimesSeries,
  getAssetsByKey,
} from '../utils';
import AreaChart from './AreaChart';
import OverflowingTitles from './OverflowingTitles';
import ComponentsPieChart from './PieChart';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  selectedCell: string[];
}

const keysToAggregate = ['total_labour_cost', 'total_part_cost'];

const TotalCostVSLabourCost = ({ selectedCell }: Props) => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  // selectors
  const { assetDashboard: assetDashboardData } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );

  // state
  const [selectedAssetsCostType, setSelectedAssetsCostType] = useState<
    string[]
  >(
    ['total_labour_cost', 'total_part_cost'] // Initialize with all available types
  );

  // fncts
  const handlePieClick = (data: any) => {
    const name = data?.name;
    const updatedClickedCells = [...selectedAssetsCostType];
    const index = updatedClickedCells.indexOf(name);

    if (index !== -1) {
      // If it's the only selected item, don't allow deselection
      if (updatedClickedCells.length === 1) return;

      // Otherwise, remove it
      updatedClickedCells.splice(index, 1);
    } else {
      // Add it
      updatedClickedCells.push(name);
    }

    setSelectedAssetsCostType(updatedClickedCells);
  };

  const pieChartDataByKey = useMemo(() => {
    const temp = getAssetsByKey(assetDashboardData.assets, selectedCell);
    return temp;
  }, [assetDashboardData?.assets, selectedCell]);

  const aggregatedSelectedAssetsTimesSeries = useMemo(() => {
    const temp = extractSelectedAssetsTimesSeries(
      assetDashboardData.timeseries,
      selectedCell,
      selectedAssetsCostType
    );
    const tempAggregatedData = aggregateCostData(temp);
    return tempAggregatedData;
  }, [assetDashboardData?.timeseries, selectedCell, selectedAssetsCostType]);

  const textColor = palette.textPrimary;

  return (
    <Base
      enableDarkTheme={enableDarkTheme}
      sx={{
        width: '100%',
        margin: '0px',
        maxWidth: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal-width columns
          gap: '8px',
          padding: '20px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ gridColumn: 'span 2', width: '100%' }}>
          <Base
            sx={{
              height: '500px',
              maxHeight: '500px',
              margin: '0px',
              backgroundColor: enableDarkTheme
                ? palette.toolBarBackground
                : palette.toolBarBackground,
            }}
          >
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                  paddingTop: '30px',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '15px',
                    color: textColor,
                  }}
                >
                  {`Labour Cost vs Part Cost for `}
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '14px',
                    color: textColor,
                  }}
                >
                  {`${selectedCell?.slice(0, 2)?.join(', ')}`}
                </div>
              </div>
              <OverflowingTitles allTitles={selectedCell} overflowAfter={2} />
            </div>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <ComponentsPieChart
                data={pieChartDataByKey}
                handlePieClick={handlePieClick}
                selectedAssetsCostType={selectedAssetsCostType}
                enableDarkTheme={enableDarkTheme}
                keysToAggregate={keysToAggregate}
              />
            </div>
          </Base>
        </div>
        <div style={{ gridColumn: 'span 3', width: '100%' }}>
          <Base
            sx={{
              height: '400px',
              maxHeight: '400px',
              margin: '0px',
              backgroundColor: enableDarkTheme
                ? palette.toolBarBackground
                : palette.toolBarBackground,
            }}
          >
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                  paddingTop: '30px',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '15px',
                    color: textColor,
                  }}
                >
                  {`Labour vs Part Trend for `}
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '14px',
                    color: textColor,
                  }}
                >
                  {`${selectedCell?.slice(0, 2)?.join(', ')}`}
                </div>
              </div>
              <OverflowingTitles allTitles={selectedCell} overflowAfter={2} />
            </div>

            {selectedAssetsCostType.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '20px',
                  paddingTop: '10px',
                }}
              >
                {t('please_select_an_asset_from_the_pie_chart')}
              </div>
            ) : (
              <AreaChart
                data={aggregatedSelectedAssetsTimesSeries}
                selectedAssetsCostType={selectedAssetsCostType}
              />
            )}
          </Base>
        </div>
      </div>
    </Base>
  );
};

export default TotalCostVSLabourCost;
